import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import AppShell from './components/AppShell';
import ErrorBoundary from './components/ErrorBoundary';
import Icon from './components/Icon';
import { ToastProvider } from './components/Toast';
import { Card, IconCircle } from './components/ui';
import Ledger from './routes/Ledger';
import Today from './routes/Today';
import {
  ApiError,
  cachedMe,
  clearCachedMe,
  getMe,
  hadSessionHere,
  markHadSession,
  setUnauthorizedHandler,
} from './lib/api';
import type { MeHousehold, MeResponse } from './lib/api';

// The two landings (Today at /, the Ledger at /ledger) + the shared
// shell load eagerly; the editors and settings are code-split
// (japan-trip App.tsx pattern).
const ExpenseEditor = lazy(() => import('./routes/ExpenseEditor'));
const Settle = lazy(() => import('./routes/Settle'));
const Settings = lazy(() => import('./routes/Settings'));
const Alerts = lazy(() => import('./routes/Alerts'));
const BankAccounts = lazy(() => import('./routes/BankAccounts'));
const Trips = lazy(() => import('./routes/Trips'));
const Inbox = lazy(() => import('./routes/Inbox'));
const Plans = lazy(() => import('./routes/Plans'));
const Lists = lazy(() => import('./routes/Lists'));
// Feedback about any of the apps (docs/feedback/README.md): a utility
// page linked from the rail and Settings, not a section.
const Feedback = lazy(() => import('./routes/Feedback'));
// The house sections (H3a) carry marked + DOMPurify; Today's "Due
// soon" card doesn't need them.
const Home = lazy(() => import('./routes/Home'));
const Manuals = lazy(() => import('./routes/Home').then((m) => ({ default: m.Manuals })));
const Devices = lazy(() => import('./routes/Home').then((m) => ({ default: m.Devices })));
const Device = lazy(() => import('./routes/Device'));
const HousePage = lazy(() => import('./routes/HousePage'));
const Dogs = lazy(() => import('./routes/Dogs'));
const Car = lazy(() => import('./routes/Car'));

// ─── household context ───────────────────────────────────────────────
// Everything below the gates renders with a resolved MeResponse + the
// active household. Routes read both via useApp(); refreshMe() re-probes
// /api/me after writes that change meta (name/accent/members), and
// switchHousehold() changes the active one (Settings, >1 household).

interface AppCtx {
  me: MeResponse;
  household: MeHousehold;
  refreshMe: () => Promise<void>;
  switchHousehold: (householdId: string) => void;
}
const Ctx = createContext<AppCtx | null>(null);

export function useApp(): AppCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp() outside provider');
  return v;
}

// Centered message card used by the gates and fallbacks.
export function MessageCard({
  icon = 'info',
  title,
  children,
}: {
  icon?: string;
  title: string;
  children?: ReactNode;
}) {
  return (
    <Card>
      <div className="empty">
        <IconCircle icon={icon} tone="accent" iconSize={22} />
        <h1 className="empty-title">{title}</h1>
        {children}
      </div>
    </Card>
  );
}

function NotFound() {
  return (
    <AppShell>
      <MessageCard title="Page not found">
        <p className="empty-body">That address doesn't match anything in WaageWu.</p>
        <a className="btn btn-secondary" href="/">
          Back to Today
        </a>
      </MessageCard>
    </AppShell>
  );
}

// Shown when the visitor isn't authenticated. /signin is the one CF
// Access-gated path; it bounces back to `next` after edge auth.
function SignInScreen() {
  const loc = useLocation();
  const next = encodeURIComponent(loc.pathname + loc.search);
  const lapsed = hadSessionHere();
  return (
    <AppShell>
      <MessageCard icon="account" title={lapsed ? 'Session expired' : 'Sign in to continue'}>
        <p className="empty-body">Your household ledger is private. Sign in to view and edit it.</p>
        <a className="btn btn-primary" href={`/signin?next=${next}`}>
          Sign in
        </a>
      </MessageCard>
    </AppShell>
  );
}

// Auth probe at app load, revalidated on window focus / tab-visible.
// Optimistic auth: a returning member has a cached /api/me under
// ww.web.me.v1, so we render immediately and revalidate behind the app
// rather than blocking first paint. 401/403 → clear cache → SignInScreen.
// (japan-trip AuthGate skeleton, guest machinery removed.)
const REVALIDATE_MIN_GAP_MS = 5_000;

function AuthGate({ children }: { children: (me: MeResponse, refresh: () => Promise<void>) => ReactNode }) {
  const cached = useMemo(() => cachedMe(), []);
  const [state, setState] = useState<'loading' | 'authed' | 'anon' | 'error'>(
    cached ? 'authed' : 'loading',
  );
  const [me, setMe] = useState<MeResponse | null>(cached);
  const [message, setMessage] = useState('');

  const stateRef = useRef(state);
  stateRef.current = state;
  // Overlapping probes resolve out of order (focus fires during a slow
  // initial probe); only the newest one may apply state.
  const probeSeq = useRef(0);
  const lastProbeAt = useRef(0);

  const probe = useCallback(
    (kind: 'initial' | 'revalidate'): Promise<void> => {
      const seq = ++probeSeq.current;
      lastProbeAt.current = Date.now();
      return getMe()
        .then((res) => {
          if (seq !== probeSeq.current) return;
          markHadSession();
          setMe(res);
          setState('authed');
        })
        .catch((err) => {
          if (seq !== probeSeq.current) return;
          if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
            clearCachedMe();
            setMe(null);
            setState('anon');
          } else if (kind === 'initial' && !cached) {
            // Network/5xx with no cache to fall back on — surface it. A
            // cached returning user keeps the optimistic shell; their
            // route's own fetches surface any real failure.
            setMessage(err instanceof Error ? err.message : 'Failed to load.');
            setState('error');
          }
        });
    },
    [cached],
  );

  useEffect(() => {
    probe('initial');
  }, [probe]);

  // A 401 during use (expired session mid-write) flips straight to the
  // sign-in screen instead of surfacing as a cryptic save error.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearCachedMe();
      setMe(null);
      setState('anon');
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Re-probe when the tab regains focus/visibility — promotes a stale
  // sign-in tab without a manual reload. Throttled against flapping.
  useEffect(() => {
    const maybeRevalidate = () => {
      if (document.visibilityState !== 'visible') return;
      if (stateRef.current === 'loading') return;
      if (Date.now() - lastProbeAt.current < REVALIDATE_MIN_GAP_MS) return;
      probe('revalidate');
    };
    window.addEventListener('focus', maybeRevalidate);
    document.addEventListener('visibilitychange', maybeRevalidate);
    return () => {
      window.removeEventListener('focus', maybeRevalidate);
      document.removeEventListener('visibilitychange', maybeRevalidate);
    };
  }, [probe]);

  const refresh = useCallback(() => probe('revalidate'), [probe]);

  if (state === 'loading') {
    return (
      <AppShell>
        <p className="loading">Loading…</p>
      </AppShell>
    );
  }
  if (state === 'error') {
    return (
      <AppShell>
        <MessageCard title="Couldn't load WaageWu">
          <p className="empty-body">{message}</p>
          <button className="btn btn-secondary" type="button" onClick={() => window.location.reload()}>
            Try again
          </button>
        </MessageCard>
      </AppShell>
    );
  }
  if (state === 'anon' || !me) return <SignInScreen />;
  return <>{children(me, refresh)}</>;
}

// ─── household selection ─────────────────────────────────────────────
// /api/me → households[]; exactly one → auto-select; several → a simple
// picker (persisted per browser). Zero → onboarding hint (households are
// created via API/MCP in v1; the real one is `waage-wu`).

const HOUSEHOLD_LS = 'ww.web.householdId';

function readStoredHouseholdId(): string | null {
  try {
    return localStorage.getItem(HOUSEHOLD_LS);
  } catch {
    return null;
  }
}

function HouseholdGate({
  me,
  refreshMe,
  children,
}: {
  me: MeResponse;
  refreshMe: () => Promise<void>;
  children: ReactNode;
}) {
  const [pickedId, setPickedId] = useState<string | null>(() => readStoredHouseholdId());

  const pick = (id: string) => {
    try {
      localStorage.setItem(HOUSEHOLD_LS, id);
    } catch {
      // private mode — in-memory selection still works this session
    }
    setPickedId(id);
  };

  const households = me.households;
  let active: MeHousehold | null = null;
  if (households.length === 1) {
    active = households[0];
  } else if (pickedId) {
    active = households.find((h) => h.householdId === pickedId) ?? null;
  }

  if (households.length === 0) {
    return (
      <AppShell>
        <MessageCard title="No household yet">
          <p className="empty-body">
            Signed in as {me.email}, but this account isn't a member of any household. Ask the
            owner to add you, then reload.
          </p>
        </MessageCard>
      </AppShell>
    );
  }

  if (!active) {
    return (
      <AppShell>
        <div className="page-head">
          <div className="page-head-text">
            <h1 className="page-title">Pick a household</h1>
            <p className="page-sub">You're a member of more than one. You can switch in Settings.</p>
          </div>
        </div>
        <Card>
          {households.map((h) => (
            <button key={h.householdId} type="button" className="row" onClick={() => pick(h.householdId)}>
              <IconCircle letter={h.name} tone="accent" />
              <span className="row-body">
                <span className="row-title">{h.name}</span>
                <span className="row-meta">{h.balance.line}</span>
              </span>
              <Icon name="chevronRight" size={18} className="row-chevron" />
            </button>
          ))}
        </Card>
      </AppShell>
    );
  }

  const value: AppCtx = { me, household: active, refreshMe, switchHousehold: pick };
  // The accent wrapper carries the per-household accent class so every
  // var(--accent) below it (buttons, chips, calendar, focus rings)
  // follows the household's color. 'food' (indigo) is the default.
  return (
    <Ctx.Provider value={value}>
      <div className={`accent-${active.accent || 'food'}`}>
        <ToastProvider>{children}</ToastProvider>
      </div>
    </Ctx.Provider>
  );
}

// Suspense fallback while a lazy route chunk downloads — mounts the
// shell so the chrome stays put and only the content area swaps.
function RouteFallback() {
  return (
    <AppShell>
      <p className="loading">Loading…</p>
    </AppShell>
  );
}

export default function App() {
  const location = useLocation();
  return (
    <AuthGate>
      {(me, refreshMe) => (
        <HouseholdGate me={me} refreshMe={refreshMe}>
          <ErrorBoundary key={location.pathname}>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Today />} />
                <Route path="/ledger" element={<Ledger />} />
                <Route path="/expense/new" element={<ExpenseEditor />} />
                <Route path="/expense/:id" element={<ExpenseEditor />} />
                <Route path="/trips" element={<Trips />} />
                <Route path="/inbox" element={<Inbox />} />
                <Route path="/plans" element={<Plans />} />
                <Route path="/lists" element={<Lists />} />
                <Route path="/lists/:id" element={<Lists />} />
                <Route path="/home" element={<Home />} />
                <Route path="/home/manuals" element={<Manuals />} />
                <Route path="/home/devices" element={<Devices />} />
                <Route path="/home/devices/:id" element={<Device />} />
                <Route path="/home/page/*" element={<HousePage section="home" />} />
                <Route path="/dogs" element={<Dogs />} />
                <Route path="/dogs/page/*" element={<HousePage section="dogs" />} />
                <Route path="/car" element={<Car />} />
                <Route path="/car/page/*" element={<HousePage section="car" />} />
                <Route path="/settle" element={<Settle />} />
                <Route path="/settle/:id" element={<Settle />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/settings/alerts" element={<Alerts />} />
                <Route path="/settings/bank" element={<BankAccounts />} />
                <Route path="/feedback" element={<Feedback />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </HouseholdGate>
      )}
    </AuthGate>
  );
}
